import axios, { AxiosInstance } from 'axios'
import type { ExportDescription, ExportFormat } from '../components/export/types'
import { currentLanguage } from '../i18n'

/** A linked WhatsApp number as the settings pages show it (never its keys) */
export type LinkState = 'STARTING' | 'WAITING_QR' | 'LINKED' | 'LOGGED_OUT' | 'UNREACHABLE' | 'STOPPED'
export interface LinkView {
  state: LinkState; number: string | null; linkedAt: string | null; lastCheckAt: string | null; lastError: string | null
  /** the QR code (a data: URL) while it waits for one */
  qr: string | null; tested: boolean; licenseKeySet: boolean
}

// Use relative URL in production so API calls inherit the page protocol (avoids mixed content).
// In dev with Vite proxy, relative /api/v1 is proxied to localhost:3001.
const BASE = import.meta.env.VITE_API_URL ?? '/api/v1'

export const api: AxiosInstance = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
})

// ─── Inactivity ──────────────────────────────────────────────────────────────
// Every request says how long ago the person last used the page (a click, a key, a scroll, a
// touch); the API signs the session out after the person's inactivity limit. Background
// refreshes say so too, so they do not count as use.
let lastAction = Date.now()
for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart']) window.addEventListener(ev, () => { lastAction = Date.now() }, { passive: true, capture: true })
export const idleSeconds = () => Math.floor((Date.now() - lastAction) / 1000)

// Attach token from store on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers['X-Idle-Seconds'] = String(idleSeconds())
  // the language in use: the server answers (errors, labels) in it
  config.headers['Accept-Language'] = currentLanguage()
  return config
})

// Auto-refresh on 401.
// The refresh token is in a cookie this code cannot read (HttpOnly) and the server rotates it on
// every use, so several requests failing at once — in this tab or another — must share ONE refresh:
// a second call with the old token is taken for a stolen copy and ends the session.
let refreshing: Promise<string> | null = null

/**
 * Back to the sign-in page; `reason` = idle: signed out after a time without activity; client_window: a
 * client person whose event's sign-in window closed
 */
let signingOut = false
/** the reason a sign-out was for, kept for the sign-in page (see signOut) */
export const SIGN_OUT_REASON = 'velanox-sign-out-reason'
export function signOut(reason?: 'idle' | 'client_window') {
  // once: several requests refused together each land here, and the first one's reason is the one
  if (signingOut) return
  signingOut = true
  // kept for the sign-in page too: emptying the store below lets the page's guards move it to /login
  // themselves, and that move can land instead of the full load of /login?reason=…
  try { if (reason) sessionStorage.setItem(SIGN_OUT_REASON, reason) } catch { /* the address still carries it */ }
  localStorage.removeItem('accessToken'); localStorage.removeItem('velanox-auth')
  const go = () => { if (!window.location.pathname.startsWith('/login')) window.location.href = reason ? `/login?reason=${reason}` : '/login' }
  // the store too: a later change to it would write the sign-in back, and the sign-in page take it as signed in
  void import('../store/auth').then(m => m.useAuthStore.getState().logout()).catch(() => undefined).finally(go)
}

async function refreshSession(): Promise<string> {
  const startedWith = localStorage.getItem('accessToken')
  const run = async (): Promise<string> => {
    // another tab refreshed while this one waited for the lock: use its result
    const now = localStorage.getItem('accessToken')
    if (now && now !== startedWith) return now
    const res = await axios.post(`${BASE}/auth/refresh`, {}, { headers: { 'X-Idle-Seconds': String(idleSeconds()) } })
    const { accessToken } = res.data.data
    localStorage.setItem('accessToken', accessToken)
    const { useAuthStore } = await import('../store/auth')
    useAuthStore.setState({ accessToken })
    return accessToken
  }
  return navigator.locks ? navigator.locks.request('velanox-refresh', run) : run()
}

// ─── The password asked again ────────────────────────────────────────────────
// Sensitive actions need the password confirmed in the last 10 minutes. The prompt
// (components/ReauthPrompt) registers here; a refused request is repeated once it is confirmed.
type Ask = () => Promise<boolean>
let askPassword: Ask | null = null
export const onReauth = (ask: Ask | null) => { askPassword = ask }
let asking: Promise<boolean> | null = null

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config
    // A file download (responseType blob) gets its JSON error as a Blob too: read it, or the
    // checks below never see its code — the password was never asked and the download just failed
    const body = err.response?.data
    if (body instanceof Blob && body.type.includes('json')) err.response.data = await body.text().then(JSON.parse).catch(() => body)
    if (err.response?.status === 401 && err.response.data?.code === 'idle') { signOut('idle'); return Promise.reject(err) }
    if (err.response?.status === 401 && err.response.data?.code === 'client_window') { signOut('client_window'); return Promise.reject(err) }
    if (err.response?.status === 403 && err.response.data?.code === 'reauth_required' && original && !original._reauth && askPassword) {
      original._reauth = true
      const ok = await (asking ??= askPassword().finally(() => { asking = null }))
      return ok ? api(original) : Promise.reject(err)
    }
    // sign-in calls answer for themselves (a phone's key refused is no session to refresh)
    const isAuthCall = /\/auth\/(login|refresh|set-password|forgot-password|reauth|app-devices\/session|sso\/handoff)/.test(original?.url ?? '')
    if (err.response?.status !== 401 || !original || original._retry || isAuthCall) return Promise.reject(err)
    original._retry = true
    try {
      // A 401 for a request that left BEFORE a refresh which has since finished is
      // stale: the session already has a newer token (this tab's, or another tab's
      // through localStorage). Refreshing again would rotate the session a second
      // time and cut off the requests just retried with the first new token.
      const sent = String(original.headers?.Authorization ?? '').replace(/^Bearer /, '')
      const current = localStorage.getItem('accessToken')
      const token = !refreshing && current && current !== sent
        ? current
        : await (refreshing ??= refreshSession().finally(() => { refreshing = null }))
      original.headers.Authorization = `Bearer ${token}`
    } catch (e) {
      // A rejected refresh, or none possible (another tab logged out), ends the session —
      // a network blip does not
      const code = axios.isAxiosError(e) ? e.response?.data?.code : undefined
      if (!axios.isAxiosError(e) || e.response?.status === 401) signOut(code === 'idle' || code === 'client_window' ? code : undefined)
      return Promise.reject(err)
    }
    // Refused again with a fresh token: this session is not accepted any more (a removed or
    // deactivated member). It used to leave the person on a shell whose every page failed.
    return api(original).catch(e2 => {
      if (axios.isAxiosError(e2) && e2.response?.status === 401) signOut()
      return Promise.reject(e2)
    })
  }
)

// ─── Typed API helpers ────────────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string, companySlug?: string) =>
    api.post('/auth/login', { email, password, companySlug }),
  /** null: Platform administration (global admins) */
  switchCompany: (companyId: string | null) => api.post('/auth/switch-company', { companyId }),
  /** into one of the person's client views (a rental customer's) */
  switchClient: (clientPersonId: string) => api.post('/auth/switch-client', { clientPersonId }),
  /** the landing page: the companies to enter, the client views, and Platform administration */
  destinations: () => api.get('/auth/destinations'),
  logout: () => api.post('/auth/logout'),
  reauth: (password: string) => api.post('/auth/reauth', { password }),
  ssoStart: (data: { slug?: string; email?: string; return?: string; appChallenge?: string }) => api.post('/auth/sso/start', data),
  /** the mobile app: single sign-on's one-time code traded for the session (with the app's verifier) */
  ssoHandoff: (code: string, verifier: string) => api.post('/auth/sso/handoff', { code, verifier }),
  /** the mobile app's phones: added after a sign-in, opened with their key, listed and removed */
  addPhone: (body: { name: string; model?: string; platform: 'ios' | 'android' }) => api.post('/auth/app-devices', body),
  unlockPhone: (id: string, key: string) => api.post('/auth/app-devices/session', { id, key }),
  phones: () => api.get('/auth/app-devices'),
  removePhone: (id: string) => api.delete(`/auth/app-devices/${id}`),
  /** push on this phone: its token (only from this phone's own session), or off */
  setPush: (id: string, token: string) => api.put(`/auth/app-devices/${id}/push`, { token }),
  clearPush: (id: string) => api.delete(`/auth/app-devices/${id}/push`),
  /** an admin's rental shortage pushes (null: not theirs to choose) */
  pushSettings: () => api.get('/auth/app-devices/push-settings'),
  setPushSettings: (body: { shortages: boolean }) => api.put('/auth/app-devices/push-settings', body),
  // app: in the mobile app — the address is for the phone's browser sheet, which comes back to the app
  ssoLink: (app?: boolean) => api.post('/auth/sso/link', app ? { app } : {}),
  ssoReauth: (ret: string, app?: boolean) => api.post('/auth/sso/reauth', { return: ret, ...(app ? { app } : {}) }),
  protection: () => api.get('/auth/protection'),
  updateProtection: (data: { idleMinutes?: number | null; signInAlerts?: boolean }) => api.patch('/auth/protection', data),
  me: () => api.get('/auth/me'),
  /** the language in use, the person's own choice and the company's */
  language: () => api.get('/auth/language'),
  updateProfile: (data: Record<string, unknown>) => api.patch('/auth/profile', data),
  forgotPassword: (email: string, companySlug?: string) => api.post('/auth/forgot-password', { email, companySlug }),
  setPassword: (token: string, password: string) => api.post('/auth/set-password', { token, password }),
  changePassword: (currentPassword: string, newPassword: string) => api.post('/auth/change-password', { currentPassword, newPassword }),
  sendPhoneCode: () => api.post('/auth/profile/phone/send-code'),
  verifyPhone: (code: string) => api.post('/auth/profile/phone/verify', { code }),
  // two-factor: during a sign-in with the challenge the password step returned, or signed in without it
  verifyCode: (challenge: string, code: string) => api.post('/auth/2fa/verify', { challenge, code }),
  setupTwoFactor: (challenge?: string) => api.post('/auth/2fa/setup', challenge ? { challenge } : {}),
  enableTwoFactor: (setupToken: string, code: string, challenge?: string) => api.post('/auth/2fa/enable', { setupToken, code, ...(challenge ? { challenge } : {}) }),
  twoFactor: () => api.get('/auth/2fa'),
  newRecoveryCodes: (code: string) => api.post('/auth/2fa/recovery-codes', { code }),
  disableTwoFactor: (password: string, code: string) => api.delete('/auth/2fa', { data: { password, code } }),
  sessions: () => api.get('/auth/sessions'),
  endSession: (id: string) => api.delete(`/auth/sessions/${id}`),
  endOtherSessions: () => api.post('/auth/sessions/end-others', {}),
}

export const monitoringApi = {
  overview: (period = '24h') => api.get(`/monitoring/overview?period=${period}`),
  site: (siteId: string) => api.get(`/monitoring/sites/${siteId}`),
  history: (siteId: string, period = '24h') => api.get(`/monitoring/sites/${siteId}/history?period=${period}`),
  /** every site the person may see, added up: the speeds per time slot and the data used */
  companyHistory: (period = '24h') => api.get(`/monitoring/history?period=${period}`),
  alerts: (resolved = false, page = 1, limit = 20) => api.get(`/monitoring/alerts?resolved=${resolved}&page=${page}&limit=${limit}`),
  /** sort: a column and its direction (none: usage, most first) */
  clients: (siteId: string, page = 1, sort?: { key: string; dir: string }) =>
    api.get(`/monitoring/sites/${siteId}/clients`, { params: { page, limit: 50, ...(sort ? { sort: sort.key, dir: sort.dir } : {}) } }),
  syncSite: (siteId: string) => api.post(`/monitoring/sites/${siteId}/sync`),
  testSite: (siteId: string) => api.get(`/monitoring/sites/${siteId}/test`),
  topology: (siteId: string) => api.get(`/monitoring/sites/${siteId}/topology`),
  dpi: (siteId: string) => api.get(`/monitoring/sites/${siteId}/dpi`),
}

/** Export on every page: the server writes what a page describes; the file's name comes with it */
export const exportsApi = {
  download: async (format: ExportFormat, description: ExportDescription): Promise<{ blob: Blob; name: string }> => {
    const r = await api.post('/exports', { format, description }, { responseType: 'blob' })
    const name = /filename="([^"]+)"/.exec(String(r.headers['content-disposition'] ?? ''))?.[1] ?? `velanox-export.${format}`
    return { blob: r.data as Blob, name }
  },
}

export const sitesApi = {
  /** push first: this person has the site's page open (their open outages there count as seen) */
  seen: (siteId: string) => api.post(`/sites/${siteId}/seen`),
  /** brand: unifi, mikrotik, teltonika, peplink or velanox; q: part of the name; sort and dir: a column (all on the server, so paging follows);
   *  archived: the sites archived with their closed event instead of the sites in use */
  list: (page = 1, limit = 20, filter: { brand?: string; q?: string; sort?: string; dir?: string; archived?: boolean } = {}) =>
    api.get('/sites', { params: { page, limit, ...(filter.brand ? { brand: filter.brand } : {}), ...(filter.q ? { q: filter.q } : {}), ...(filter.sort ? { sort: filter.sort, dir: filter.dir } : {}), ...(filter.archived ? { archived: '1' } : {}) } }),
  /** every site this person sees (id, name, provider) — for pickers */
  /** every site for a picker; vpn: only those with their VPN connection, with its address */
  options: (o: { vpn?: boolean } = {}) => api.get('/sites/options', o.vpn ? { params: { vpn: 1 } } : undefined),
  get: (id: string) => api.get(`/sites/${id}`),
  create: (data: Record<string, unknown>) => api.post('/sites', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/sites/${id}`, data),
  /** a UniFi controller's sign-in: an API key, or a local account (a self-hosted Network Server has no keys) */
  updateCredentials: (id: string, data: { unifiApiKey?: string; unifiUsername?: string; unifiPassword?: string }) => api.patch(`/sites/${id}/credentials`, data),
  delete: (id: string) => api.delete(`/sites/${id}`),
  alerts: (id: string, page = 1) => api.get(`/sites/${id}/alerts?page=${page}`),
  acknowledgeAlert: (siteId: string, alertId: string) =>
    api.post(`/sites/${siteId}/alerts/${alertId}/acknowledge`),
}

export const usersApi = {
  list: (page = 1, q = '') => api.get(`/users?page=${page}&q=${encodeURIComponent(q)}`),
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: Record<string, unknown>) => api.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  roles: () => api.get('/users/roles/list'),
  createRole: (data: Record<string, unknown>) => api.post('/users/roles', data),
  updateRole: (id: string, data: Record<string, unknown>) => api.patch(`/users/roles/${id}`, data),
  deleteRole: (id: string) => api.delete(`/users/roles/${id}`),
  resendInvite: (id: string) => api.post(`/users/${id}/resend-invite`, {}),
  resetTwoFactor: (id: string) => api.post(`/users/${id}/reset-2fa`, {}),
}

export const companiesApi = {
  get: (id: string) => api.get(`/companies/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/companies/${id}`, data),
  /** the logo as a data: URL (scaled by the page first); the answer holds its new address */
  setLogo: (id: string, image: string) => api.put(`/companies/${id}/logo`, { image }),
  removeLogo: (id: string) => api.delete(`/companies/${id}/logo`),
  /** a picture's backdrops: 'default', '#rrggbb' or null (none); only what is sent changes */
  setBackdrops: (id: string, kind: 'logo' | 'banner', b: { backdropDark?: string | null; backdropLight?: string | null }) => api.patch(`/companies/${id}/${kind}`, b),
  // the banner logo: the topbar's picture instead of the company's name
  setBanner: (id: string, image: string) => api.put(`/companies/${id}/banner`, { image }),
  removeBanner: (id: string) => api.delete(`/companies/${id}/banner`),
  branding: (id: string) => api.get(`/companies/${id}/branding`),
  updateBranding: (id: string, data: Record<string, unknown>) => api.put(`/companies/${id}/branding`, data),
  settings: (id: string) => api.get(`/companies/${id}/settings`),
  /** target linked: the linked number's own test (only once that choice is saved) */
  testWhatsapp: (id: string, data: { to: string; target?: 'linked' }) => api.post(`/companies/${id}/whatsapp/test`, data),
  /** the company's linked WhatsApp number (licence module "Linked WhatsApp number"): read now, Link / Re-link, Unlink */
  whatsappLink: (id: string) => api.get(`/companies/${id}/whatsapp/link`).then(r => r.data.data as LinkView | null),
  linkWhatsapp: (id: string, body: { licenseKey?: string | null }) => api.post(`/companies/${id}/whatsapp/link`, body).then(r => r.data.data as LinkView),
  /** the licence key of the number linked now (started again with its session: no new QR) */
  setWhatsappKey: (id: string, body: { licenseKey?: string | null }) => api.patch(`/companies/${id}/whatsapp/link`, body).then(r => r.data.data as LinkView),
  unlinkWhatsapp: (id: string) => api.delete(`/companies/${id}/whatsapp/link`),
  testRcs: (id: string, data: { to: string }) => api.post(`/companies/${id}/rcs/test`, data),
  testSmtp: (id: string) => api.post(`/companies/${id}/smtp/test`, {}),
  updateSettings: (id: string, data: Record<string, unknown>) => api.put(`/companies/${id}/settings`, data),
  publicBranding: (slug: string) => api.get(`/companies/public/${slug}`),
  byHost: (host: string) => api.get(`/companies/public/by-host?host=${encodeURIComponent(host)}`),
  renewLicense: (id: string, code: string) => api.post(`/companies/${id}/license/renew`, { code }),
  domain: (id: string) => api.get(`/companies/${id}/domain`),
  checkDomain: (id: string) => api.post(`/companies/${id}/domain/check`, {}),
  mailDns: (id: string) => api.get(`/companies/${id}/mail-dns`),
  messages: (id: string) => api.get(`/companies/${id}/messages`),
  updateMessage: (id: string, key: string, data: Record<string, unknown>) => api.put(`/companies/${id}/messages/${key}`, data),
  resetMessage: (id: string, key: string) => api.delete(`/companies/${id}/messages/${key}`),
  // single sign-on (OpenID Connect)
  sso: (id: string) => api.get(`/companies/${id}/sso`),
  updateSso: (id: string, data: Record<string, unknown>) => api.put(`/companies/${id}/sso`, data),
  addSsoDomain: (id: string, domain: string) => api.post(`/companies/${id}/sso/domains`, { domain }),
  checkSsoDomain: (id: string, domainId: string) => api.post(`/companies/${id}/sso/domains/${domainId}/check`, {}),
  deleteSsoDomain: (id: string, domainId: string) => api.delete(`/companies/${id}/sso/domains/${domainId}`),
  setBreakGlass: (id: string, membershipId: string, breakGlass: boolean) => api.patch(`/companies/${id}/sso/break-glass/${membershipId}`, { breakGlass }),
}

export const templatesApi = {
  list: (type?: string, page = 1, limit = 20) => api.get(`/templates?page=${page}&limit=${limit}${type ? `&type=${type}` : ''}`),
  get: (id: string) => api.get(`/templates/${id}`),
  createFromPull: (data: Record<string, unknown>) => api.post('/templates/pull', data),
  createManual: (data: Record<string, unknown>) => api.post('/templates/manual', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/templates/${id}`, data),
  delete: (id: string) => api.delete(`/templates/${id}`),
  apply: (id: string, siteIds: string[]) => api.post(`/templates/${id}/apply`, { siteIds }),
  applicationStatus: (id: string) => api.get(`/templates/applications/${id}`),
  suggestions: () => api.get('/templates/suggestions'),
  blueprints: (family: string) => api.get(`/templates/blueprints?family=${family}`),
  blueprint: (type: string, provider: string) => api.get(`/templates/blueprints/${type}/${provider}`),
  compatibility: (id: string) => api.get(`/templates/${id}/compatibility`),
}

export const vpnApi = {
  regenerateMyConfig: () => api.post('/vpn/my-config/regenerate', {}),
  regenerateSiteConfig: (siteId: string) => api.post(`/vpn/sites/${siteId}/regenerate`, {}),
  myConfig: () => api.get('/vpn/my-config', { responseType: 'blob' }),
  myStatus: () => api.get('/vpn/my-status'),
  siteConfig: (siteId: string) => api.get(`/vpn/sites/${siteId}/config`, { responseType: 'blob' }),
  siteStatus: (siteId: string) => api.get(`/vpn/sites/${siteId}/status`),
  /** the site's certificate, key and CA as .p12 (RouterOS), with a passphrase made for this download */
  siteP12: (siteId: string) => api.post(`/vpn/sites/${siteId}/p12`, {}),
  accounts: () => api.get('/vpn/accounts'),
  provision: () => api.post('/vpn/provision', {}),
  adminProvision: (membershipId: string) => api.post(`/vpn/admin/provision/${membershipId}`, {}),
  toggleAccount: (membershipId: string) => api.patch(`/vpn/accounts/${membershipId}/toggle`, {}),
}

export const apiKeysApi = {
  list: () => api.get('/api-keys'),
  create: (data: Record<string, unknown>) => api.post('/api-keys', data),
  revoke: (id: string) => api.delete(`/api-keys/${id}`),
}

export const webhooksApi = {
  list: () => api.get('/webhooks'),
  create: (data: Record<string, unknown>) => api.post('/webhooks', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/webhooks/${id}`, data),
  delete: (id: string) => api.delete(`/webhooks/${id}`),
  test: (id: string) => api.post(`/webhooks/${id}/test`, {}),
}

/** a model seen, as Admin → Models lists it */
export interface ModelRow {
  brand: string; model: string; isNew: boolean; devices: number; companies: string[]
  firmware: { version: string; devices: number }[]; firstSeenAt: string; lastSeenAt: string
  gaps: {
    id: string; kind: string; value: string; times: number; firstSeenAt: string; lastSeenAt: string
    exampleSite: { id: string; name: string; provider: string; company: string } | null
    /** where its brand keeps what it handles (from the repository's root); no list for Teltonika's parts */
    handledIn: { list?: string; file: string } | null
  }[]
}

/** An installation sending through this one's push relay: counted, never its texts or tokens */
export interface RelayInstallation {
  id: string; name: string; address: string; createdAt: string; lastPushAt: string | null
  today: number; month: number; hourlyLimit: number; blocked: boolean
}
/** How pushes go from this installation (Administration → Settings → Push) */
export interface PushInUse { mode: 'direct' | 'relay' | 'off'; relayOn?: boolean; relay?: { url: string; state: 'not_signed_up' | 'signed_up' | 'blocked' } }

export const adminApi = {
  stats: () => api.get('/admin/stats'),
  /** the fleet's models: those in use, or (gone) those not reported for 90 days */
  models: (q: { brand?: string; gone?: boolean }) => api.get('/admin/models', { params: { brand: q.brand, gone: q.gone ? '1' : undefined } }).then(r => r.data.data as ModelRow[]),
  /** Mark resolved: one Partly read line cleared (noted again when a device meets it again) */
  resolveModelGap: (id: string) => api.delete(`/admin/models/gaps/${id}`),
  jobs: (status?: string) => api.get(`/admin/jobs${status ? `?status=${status}` : ''}`),
  /** the platform's audit log, newest first (20 a page unless a limit is given) */
  audit: (companyId?: string, page?: number, limit?: number) => api.get('/admin/audit', { params: { companyId, page, limit } }),
  platformSettings: () => api.get('/admin/platform-settings'),
  updatePlatformSettings: (data: Record<string, unknown>) => api.patch('/admin/platform-settings', data),
  /** sort and dir: a column (on the server: the list is paged) */
  companies: (page = 1, q = '', sort?: { key: string; dir: string }, limit = 20) =>
    api.get('/companies', { params: { page, limit, q, ...(sort ? { sort: sort.key, dir: sort.dir } : {}) } }),
  /** every company (id, name, slug) — for pickers */
  companyOptions: () => api.get('/companies/options'),
  companyUsers: (companyId: string) => api.get(`/admin/companies/${companyId}/users`),
  resetUserTwoFactor: (identityId: string) => api.post(`/admin/users/${identityId}/reset-2fa`, {}),
  resetAdminTwoFactor: (adminId: string) => api.post(`/admin/global-admins/${adminId}/reset-2fa`, {}),
  createCompany: (data: Record<string, unknown>) => api.post('/companies', data),
  updateLicense: (id: string, data: Record<string, unknown>) => api.patch(`/companies/${id}/license`, data),
  resendUserInvite: (userId: string) => api.post(`/admin/users/${userId}/resend-invite`, {}),
  testEmail: (data: Record<string, unknown>) => api.post('/admin/test-email', data),
  /** target linked: the linked number's own test (once that choice is saved); fallback: the linked number's fallback */
  testWhatsapp: (data: { to: string; target?: 'linked' | 'fallback' }) => api.post('/admin/whatsapp/test', data),
  /** the platform's linked WhatsApp number: read now, Link / Re-link, Unlink; and the companies' numbers on this server */
  whatsappLink: () => api.get('/admin/whatsapp/link').then(r => r.data.data as LinkView | null),
  linkWhatsapp: (body: { licenseKey?: string | null }) => api.post('/admin/whatsapp/link', body).then(r => r.data.data as LinkView),
  setWhatsappKey: (body: { licenseKey?: string | null }) => api.patch('/admin/whatsapp/link', body).then(r => r.data.data as LinkView),
  unlinkWhatsapp: () => api.delete('/admin/whatsapp/link'),
  whatsappLinks: () => api.get('/admin/whatsapp/links').then(r => r.data.data as Array<{ companyName: string; number: string | null; state: LinkState; linkedAt: string | null }>),
  testRcs: (data: { to: string }) => api.post('/admin/rcs/test', data),
  /** a test push to the admin's own phones, the way pushes go from this installation */
  testPush: () => api.post('/admin/push/test').then(r => r.data.data as { sent: number; errors: string[] }),
  /** the installations that send their pushes through this one */
  pushRelay: () => api.get('/admin/push-relay').then(r => r.data.data as RelayInstallation[]),
  updatePushRelay: (id: string, body: { hourlyLimit?: number; blocked?: boolean }) => api.put(`/admin/push-relay/${id}`, body),
  whatsappTemplates: () => api.get('/admin/whatsapp/templates'),
  syncWhatsappTemplates: () => api.post('/admin/whatsapp/templates/sync', {}),
  getCompany: (id: string) => api.get(`/companies/${id}`),
  updateCompany: (id: string, data: Record<string, unknown>) => api.patch(`/companies/${id}`, data),
  getCompanySettings: (id: string) => api.get(`/companies/${id}/settings`),
  updateCompanySettings: (id: string, data: Record<string, unknown>) => api.put(`/companies/${id}/settings`, data),
  getCompanyBranding: (id: string) => api.get(`/companies/${id}/branding`),
  updateCompanyBranding: (id: string, data: Record<string, unknown>) => api.put(`/companies/${id}/branding`, data),
  // Companies
  deleteCompany: (id: string) => api.delete(`/admin/companies/${id}`),
  assignAdmin: (id: string, data: Record<string, unknown>) => api.post(`/admin/companies/${id}/assign-admin`, data),
  assignLicense: (companyId: string, data: Record<string, unknown>) => api.post(`/admin/companies/${companyId}/assign-license`, data),
  licenseModules: (companyId: string) => api.get(`/admin/companies/${companyId}/license-modules`),
  // License plans
  licensePlans: () => api.get('/admin/license-plans'),
  createLicensePlan: (data: Record<string, unknown>) => api.post('/admin/license-plans', data),
  updateLicensePlan: (id: string, data: Record<string, unknown>) => api.put(`/admin/license-plans/${id}`, data),
  deleteLicensePlan: (id: string) => api.delete(`/admin/license-plans/${id}`),
  // License overview
  licenseOverview: () => api.get('/admin/licenses'),
  deleteLicense: (id: string) => api.delete(`/admin/licenses/${id}`),
  notifyExpiredLicense: (id: string) => api.post(`/admin/licenses/${id}/notify-expiry`, {}),
  deleteExpiredLicenses: () => api.delete('/admin/license-keys/bulk/expired'),
  // License keys
  licenseKeys: () => api.get('/admin/license-keys'),
  generateLicenseKey: (data: Record<string, unknown>) => api.post('/admin/license-keys', data),
  revokeLicenseKey: (id: string) => api.delete(`/admin/license-keys/${id}`),
  deleteAvailableKeys: () => api.delete('/admin/license-keys/bulk/available'),
  // Global admins
  listGlobalAdmins: () => api.get('/admin/global-admins'),
  /** an existing account gets the rights; a new address gets an invitation */
  createGlobalAdmin: (data: { email: string; name: string; isSuperAdmin: boolean }) => api.post('/admin/global-admins', data),
  updateGlobalAdmin: (id: string, data: { name?: string; isSuperAdmin?: boolean }) => api.put(`/admin/global-admins/${id}`, data),
  deleteGlobalAdmin: (id: string) => api.delete(`/admin/global-admins/${id}`),
  // VPN
  vpnStatus: () => api.get('/admin/vpn/status'),
  vpnLogs: (lines?: number) => api.get(`/admin/vpn/logs${lines ? `?lines=${lines}` : ''}`),
}


// ─── Velanox One Speed ─────────────────────────────────────────────────────
export const velanoxApi = {
  registerSite:   (data: Record<string, unknown>) => api.post('/velanox/sites', data),
  rescanEndpoint: (siteId: string) => api.post(`/velanox/endpoints/${siteId}/rescan`),
  control:        (siteId: string, action: string, args?: Record<string, unknown>) => api.post(`/velanox/sites/${siteId}/control`, { action, ...args }),
  starlink:       (siteId: string) => api.get(`/velanox/sites/${siteId}/starlink`),
  opnsense:       (siteId: string, method: string, path: string, body?: unknown) => api.post(`/velanox/sites/${siteId}/opnsense`, { method, path, body }),
  liveSite:       (siteId: string) => api.get(`/velanox/sites/${siteId}/live`),
  updateSettings: (siteId: string, data: Record<string, unknown>) => api.patch(`/velanox/sites/${siteId}/settings`, data),
  /** a box's token (a box added through the VPN is first asked once its tunnel is up) */
  updateCredentials: (siteId: string, data: { velanoxToken?: string }) => api.patch(`/velanox/sites/${siteId}/credentials`, data),
}


// ─── Teltonika ─────────────────────────────────────────────────────────────
export const teltonikaApi = {
  registerSite: (data: Record<string, unknown>) => api.post('/teltonika/sites', data),
  /** a router by its serial, reached through its own VPN certificate (issued in the same call) */
  provision:    (data: Record<string, unknown>) => api.post('/teltonika/provision', data),
  rescanRms:    (siteId: string) => api.post(`/teltonika/rms/${siteId}/rescan`),
  liveSite:     (siteId: string) => api.get(`/teltonika/sites/${siteId}/live`),
  /** what the last sync stored of the router: shown at once while its live read comes */
  storedSite:   (siteId: string) => api.get(`/teltonika/sites/${siteId}/stored`),
  modems:       (siteId: string) => api.get(`/teltonika/sites/${siteId}/modems`),
  control:      (siteId: string, action: string, args?: Record<string, unknown>) =>
                  api.post(`/teltonika/sites/${siteId}/control`, { action, ...args }),
  /** whether the router has backup power (UPS); on: its power input read at once */
  setBackupPower: (siteId: string, on: boolean) => api.put(`/teltonika/sites/${siteId}/backup-power`, { on }),
  updateCredentials: (siteId: string, data: { teltonikaUser?: string; teltonikaToken?: string }) => api.patch(`/teltonika/sites/${siteId}/credentials`, data),
}

/** a Teltonika router's SIM slot as last read (no PIN in it) */
export type SimSlotView = {
  modemId: string; slot: number; active: boolean; iccid: string | null; operator: string | null
  state: 'LOCKED' | 'UNLOCKED' | 'BLOCKED' | 'UNKNOWN'; pinTries: number | null; pukTries: number | null
  storedPinMatches: boolean | null; uses: 'COMPANY' | 'OWN' | null; note: string | null; readAt: string
}
export type SimActionName = 'lock_company' | 'use_own' | 'change' | 'unlock' | 'unblock' | 'store'
export type SimStep = 'change_pin' | 'check_on' | 'check_off' | 'unblock' | 'store' | 'read'
export type SimPinsCardData = { companyPinSet: boolean; sims: { iccid: string; note: string | null; ownPinSet: boolean; companyPin: boolean }[] }
/** Teltonika SIM PINs (docs/superpowers/specs/2026-11-01-teltonika-sim-pin-design.md) */
export const simApi = {
  slots: (siteId: string) => api.get(`/teltonika/sites/${siteId}/sims`).then(r => r.data.data as { direct: boolean; slots: SimSlotView[] }),
  act: (siteId: string, modemId: string, slot: number, body: { action: SimActionName; currentPin?: string; newPin?: string; company?: boolean; puk?: string; note?: string; lastTry?: boolean; iccid?: string }) =>
    api.post(`/teltonika/sites/${siteId}/sims/${encodeURIComponent(modemId)}/${slot}`, body).then(r => r.data.data as { done: SimStep[]; slot: SimSlotView }),
  card: () => api.get('/teltonika/sim-pins').then(r => r.data.data as SimPinsCardData),
  setCompanyPin: (pin: string) => api.put('/teltonika/sim-pins/company', { pin }),
  reveal: (iccid?: string) => api.post('/teltonika/sim-pins/reveal', iccid ? { iccid } : {}).then(r => r.data.data.pin as string),
  removeSim: (iccid: string) => api.delete(`/teltonika/sim-pins/sims/${encodeURIComponent(iccid)}`),
}


// ─── MikroTik ──────────────────────────────────────────────────────────────
export const mikrotikApi = {
  registerSite: (data: Record<string, unknown>) => api.post('/mikrotik/sites', data),
  /** a unit by its serial, reached through its own VPN certificate (issued in the same call) */
  provision:    (data: Record<string, unknown>) => api.post('/mikrotik/provision', data),
  liveSite:     (siteId: string) => api.get(`/mikrotik/sites/${siteId}/live`),
  links:        (siteId: string) => api.get(`/mikrotik/sites/${siteId}/links`),
  renameLink:   (siteId: string, deviceId: string, name: string) => api.patch(`/mikrotik/sites/${siteId}/links/${deviceId}`, { name }),
  forgetLink:   (siteId: string, deviceId: string) => api.delete(`/mikrotik/sites/${siteId}/links/${deviceId}`),
  control:      (siteId: string, action: string) => api.post(`/mikrotik/sites/${siteId}/control`, { action }),
}

// ─── Peplink ───────────────────────────────────────────────────────────────
export const peplinkApi = {
  ic2Orgs:      (data: Record<string, unknown>) => api.post('/peplink/ic2/orgs', data),
  ic2Groups:    (data: Record<string, unknown>) => api.post('/peplink/ic2/groups', data),
  registerSite: (data: Record<string, unknown>) => api.post('/peplink/sites', data),
  rescanIc2:    (siteId: string) => api.post(`/peplink/ic2/${siteId}/rescan`),
  liveSite:     (siteId: string) => api.get(`/peplink/sites/${siteId}/live`),
  /** what the last sync stored of the router: shown at once while its live read comes */
  storedSite:   (siteId: string) => api.get(`/peplink/sites/${siteId}/stored`),
  tunnels:      (siteId: string) => api.get(`/peplink/sites/${siteId}/tunnels`),
  control:      (siteId: string, action: string, args?: Record<string, unknown>) =>
                  api.post(`/peplink/sites/${siteId}/control`, { action, ...args }),
  updateCredentials: (siteId: string, data: { peplinkUser?: string; peplinkSecret?: string }) => api.patch(`/peplink/sites/${siteId}/credentials`, data),
}


// ─── Unified config + capabilities ─────────────────────────────────────────
export const configApi = {
  allCapabilities: () => api.get('/config/capabilities'),
  capabilities:  (siteId: string) => api.get(`/config/sites/${siteId}/capabilities`),
  read:          (siteId: string, section: string) => api.get(`/config/sites/${siteId}/${section}`),
  /** later: the page shows the copy — saved for the unit's next contact without trying it */
  write:         (siteId: string, section: string, changes: Record<string, unknown>, later = false) =>
                   api.put(`/config/sites/${siteId}/${section}`, changes, later ? { params: { later: 1 } } : undefined),
  history:       (siteId: string) => api.get(`/config/sites/${siteId}/journal/history`),
  // changes saved for a unit that could not be reached (MikroTik), pushed on its next contact
  pending:       (siteId: string) => api.get(`/config/sites/${siteId}/pending`),
  discard:       (siteId: string, id: string) => api.delete(`/config/sites/${siteId}/pending/${id}`),
  decide:        (siteId: string, id: string, keep: 'mine' | 'unit') => api.post(`/config/sites/${siteId}/pending/${id}/decide`, { keep }),
  release:       (siteId: string, id: string) => api.post(`/config/sites/${siteId}/pending/${id}/release`, {}),
}

// ─── Controllers and vendor clouds (Omada) ─────────────────────────────────
export const cloudApi = {
  connections:      () => api.get('/cloud/connections'),
  createConnection: (data: Record<string, unknown>) => api.post('/cloud/connections', data),
  /** a credential left out keeps its value */
  updateConnection: (id: string, data: Record<string, unknown>) => api.patch(`/cloud/connections/${id}`, data),
  deleteConnection: (id: string) => api.delete(`/cloud/connections/${id}`),
  /** the controller's sites, each with whether it is in the dashboard already */
  vendorSites:      (id: string) => api.get(`/cloud/connections/${id}/sites`),
  addSites:         (id: string, externalSiteIds: string[]) => api.post(`/cloud/connections/${id}/sites`, { externalSiteIds }),
  /** the controller's own VPN connection (reach OWN_VPN) */
  ovpn:             (id: string) => api.get(`/cloud/connections/${id}/ovpn`, { responseType: 'blob' }),
  /** a new certificate for the controller's own VPN connection (the old .ovpn stops working) */
  regenerate:       (id: string) => api.post(`/cloud/connections/${id}/regenerate`, {}),
  site:             (siteId: string) => api.get(`/cloud/sites/${siteId}`),
  events:           (siteId: string) => api.get(`/cloud/sites/${siteId}/events`),
  deviceAction:     (siteId: string, mac: string, action: 'restart' | 'upgrade') => api.post(`/cloud/sites/${siteId}/devices/${encodeURIComponent(mac)}/${action}`, {}),
  clientAction:     (siteId: string, mac: string, action: 'block' | 'unblock' | 'reconnect') => api.post(`/cloud/sites/${siteId}/clients/${encodeURIComponent(mac)}/${action}`, {}),
  /** a site whose firmware is updated as a whole (SmartZone): its version and the newer ones the controller holds */
  siteFirmware:     (siteId: string) => api.get(`/cloud/sites/${siteId}/firmware`),
  updateSiteFirmware: (siteId: string, version: string) => api.post(`/cloud/sites/${siteId}/firmware`, { version }),
}

// ─── UniFi Site Manager (Host) ─────────────────────────────────────────────
export const unifiHostApi = {
  verify:       (data: Record<string, unknown>) => api.post('/unifi-host/verify', data),
  registerSite: (data: Record<string, unknown>) => api.post('/unifi-host/sites', data),
  rescan:       (siteId: string) => api.post(`/unifi-host/${siteId}/rescan`),
  liveSite:     (siteId: string) => api.get(`/unifi-host/sites/${siteId}/live`),
  network:      (siteId: string) => api.get(`/unifi-host/sites/${siteId}/network`),
  convertStatus: (siteId: string) => api.get(`/unifi-host/sites/${siteId}/convert`),
  convertStart:  (siteId: string) => api.post(`/unifi-host/sites/${siteId}/convert/start`, {}),
  convertFinish: (siteId: string, data: { apiKey: string; controllerUrl?: string }) => api.post(`/unifi-host/sites/${siteId}/convert/finish`, data),
  ispMetrics:   (siteId: string, type: '5m' | '1h' = '5m') => api.get(`/unifi-host/sites/${siteId}/isp-metrics?type=${type}`),
}


// ─── Outage subscriptions ──────────────────────────────────────────────────
export const subscriptionsApi = {
  list:        () => api.get('/subscriptions'),
  channels:    () => api.get('/subscriptions/channels'),
  create:      (data: Record<string, unknown>) => api.post('/subscriptions', data),
  update:      (id: string, data: Record<string, unknown>) => api.patch(`/subscriptions/${id}`, data),
  remove:      (id: string) => api.delete(`/subscriptions/${id}`),
  mute:        (id: string, deviceId: string, reason?: string) => api.post(`/subscriptions/${id}/mute/${deviceId}`, { reason }),
  unmute:      (id: string, deviceId: string) => api.delete(`/subscriptions/${id}/mute/${deviceId}`),
  test:        (id: string) => api.post(`/subscriptions/${id}/test`, {}),
  history:     (id: string, page = 1) => api.get(`/subscriptions/${id}/history?page=${page}&limit=10`),
  siteDevices: (siteId: string) => api.get(`/subscriptions/site/${siteId}/devices`),
}


// ─── Edge (HAProxy + Caddy) ────────────────────────────────────────────────
export const edgeApi = {
  status:         () => api.get('/edge/status'),
  domains:        () => api.get('/edge/domains'),
  addDomain:      (data: Record<string, unknown>) => api.post('/edge/domains', data),
  verifyDomain:   (id: string) => api.post(`/edge/domains/${id}/verify`, {}),
  removeDomain:   (id: string) => api.delete(`/edge/domains/${id}`),
  ownership:      (id: string, action: 'check' | 'override' | 'reset') => api.post(`/edge/domains/${id}/ownership/${action}`, {}),
  checkDns:       (domain: string) => api.get(`/edge/domains/check?domain=${encodeURIComponent(domain)}`),
  certificates:   () => api.get('/edge/certificates'),
  syncCaddy:      () => api.post('/edge/caddy/sync', {}),
  haproxyConfig:  () => api.get('/edge/haproxy/config'),
  vpnInstances:   () => api.get('/edge/vpn-instances'),
  provisionVpn:   (companyId: string) => api.post(`/edge/vpn-instances/${companyId}/provision`, {}),
  refreshVpnHost: (companyId: string) => api.post(`/edge/vpn-instances/${companyId}/refresh-host`, {}),
  stopVpn:        (companyId: string) => api.post(`/edge/vpn-instances/${companyId}/stop`, {}),
}
